import React, { useState, useMemo, useEffect } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import ActionReportBadge from "../ActionReportBadge/ActionReportBadge";
import IconsLegend from "../IconsLegend/IconsLegend";
import i18n from "../../../../i18n";
import styles from "../../appointmentsPage.module.css";
import {
  IAppointment,
  IAppointmentStats,
  IMonthDay,
  IWeekDay,
} from "../../types/appointments.types";
import { useAppointmentsContext } from "../../context/AppointmentsContext";
import { useAppSelector } from "../../../../services/hooks";
import { adminSelector } from "../../../../services/selectors/user.selectors";
import AppointmentStats from "../AppointmentStats/AppointmentStats";
import AppointmentDetailsModal from "../AppointmentDetailsModal/AppointmentDetailsModal";
import CreateAppointmentModal from "../CreateAppointmentModal/CreateAppointmentModal";
import MoveConfirmationModal from "../MoveConfirmationModal/MoveConfirmationModal";
import DayAppointmentsModal from "../DayAppointmentsModal/DayAppointmentsModal";
import {
  getAppointmentColors,
  getFundingSourceColor,
  truncateText,
} from "../../utils/fundingSourceColors";
import {
  getInitialDateForView,
  saveDateForView,
} from "../../utils/calendarStorage";
import { usePatchAppointmentMutation } from "../../../../services/api/utils.api";
import ExportTableButtons from "../../../../components/exportTableButtons/ExportTableButtons";
import {
  buildAppointmentsExport,
  buildAdminFilterSummary,
} from "../../utils/buildAppointmentsExport";

// Get locale based on current language
const getLocale = () => (i18n.language === "he" ? "he-IL" : "en-US");

/**
 * Monthly View Component
 *
 * Displays appointments in a monthly calendar grid.
 * Uses real API data with smart month-based caching.
 */
const MonthlyView: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(() =>
    getInitialDateForView("monthly"),
  );
  const [selectedAppointment, setSelectedAppointment] =
    useState<IAppointment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Create Appointment State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalDate, setCreateModalDate] = useState("");
  const [createModalTime, setCreateModalTime] = useState("");
  const [createModalTherapistId, setCreateModalTherapistId] = useState<
    number | undefined
  >(undefined);

  // Drag & Drop State
  const [draggedAppointment, setDraggedAppointment] =
    useState<IAppointment | null>(null);
  const [draggedFromDay, setDraggedFromDay] = useState<IMonthDay | null>(null);
  const [dragOverDay, setDragOverDay] = useState<string | null>(null);
  const [dragOverTherapistId, setDragOverTherapistId] = useState<number | null>(
    null,
  );
  const [dragOverStatus, setDragOverStatus] = useState<
    "valid" | "invalid" | "warning"
  >("valid");
  const [showMoveConfirmation, setShowMoveConfirmation] = useState(false);
  const [pendingMove, setPendingMove] = useState<{
    appointment: IAppointment;
    fromDay: IWeekDay;
    fromTime: string;
    fromEndTime: string;
    toDay: IWeekDay;
    toTime: string;
    toEndTime: string;
    toTherapistId: number;
    toTherapistGuid?: string;
    toTherapistName: string;
    warning?: string;
  } | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<IMonthDay | null>(null);
  const [tooltip, setTooltip] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);

  const admin = useAppSelector(adminSelector);

  // API hooks
  const [patchAppointment] = usePatchAppointmentMutation();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Use appointments data hook
  // Use shared appointments context (data is cached and shared across all views)
  const {
    ensureMonthLoaded,
    reloadMonth,
    filterAppointments: filterAppointmentsHook,
    therapistsList,
    companiesOptions,
    fundingSourcesList,
    filters,
    isFullscreen,
    showFiltersInFullscreen,
  } = useAppointmentsContext();

  // Load current month on mount and when month changes
  useEffect(() => {
    if (admin.companyId) {
      ensureMonthLoaded(currentDate);
    }
  }, [admin.companyId, currentDate, ensureMonthLoaded]);

  // Persist date when it changes (for navigation back)
  useEffect(() => {
    saveDateForView("monthly", currentDate);
  }, [currentDate]);

  // Get month range for filtering
  const monthStart = useMemo(() => new Date(year, month, 1), [year, month]);
  const monthEnd = useMemo(
    () => new Date(year, month + 1, 0, 23, 59, 59, 999),
    [year, month],
  );

  // Filter appointments for the month
  const appointments = useMemo(() => {
    return filterAppointmentsHook({
      dateRange: { start: monthStart, end: monthEnd },
      fakeCompanyIds: filters.fakeCompanyIds,
      therapistIds: filters.therapistIds,
      fundingSourceIds: filters.fundingSourceIds,
      patientSearch: filters.patientSearch,
      includeOtherEventsForAdminId: Number(admin.id),
    });
  }, [
    filterAppointmentsHook,
    monthStart,
    monthEnd,
    filters.fakeCompanyIds,
    filters.therapistIds,
    filters.fundingSourceIds,
    filters.patientSearch,
    admin.id,
  ]);

  // Export payload (PDF/Excel) for the currently displayed, filtered month.
  const exportProps = useMemo(
    () =>
      buildAppointmentsExport(appointments, {
        viewType: "monthly",
        rangeLabel: currentDate.toLocaleDateString(getLocale(), {
          month: "long",
          year: "numeric",
        }),
        filterSummary: buildAdminFilterSummary(filters, {
          therapistsList,
          companiesOptions,
          fundingSourcesList,
        }),
      }),
    [
      appointments,
      currentDate,
      filters,
      therapistsList,
      companiesOptions,
      fundingSourcesList,
    ],
  );

  const isTherapistFilterActive = !!(
    filters.therapistIds && filters.therapistIds.length > 0
  );

  // Determine which therapists to display - same logic as DailyView
  const displayedTherapists = useMemo(() => {
    // If therapist filter is active and has selections, show only those
    if (isTherapistFilterActive) {
      const selected = therapistsList.filter(
        (t) => filters.therapistIds!.includes(t.id) && t.isActive,
      );

      return selected;
    }

    // Check if any other filters are active
    const hasActiveFilters =
      (filters.fakeCompanyIds && filters.fakeCompanyIds.length > 0) ||
      (filters.fundingSourceIds && filters.fundingSourceIds.length > 0) ||
      (filters.patientSearch && filters.patientSearch.trim().length > 0);

    // If other filters are active, show only therapists with appointments in filtered results
    if (hasActiveFilters) {
      const therapistIdsWithAppointments = new Set(
        appointments
          .map((apt: IAppointment) => apt.therapistsId)
          .filter((id: number | undefined) => id !== undefined),
      );
      return therapistsList.filter(
        (t) => t.isActive && therapistIdsWithAppointments.has(t.id),
      );
    }

    // Otherwise show all active therapists
    return therapistsList.filter((t) => t.isActive);
  }, [
    therapistsList,
    filters.therapistIds,
    filters.fakeCompanyIds,
    filters.fundingSourceIds,
    filters.patientSearch,
    appointments,
    isTherapistFilterActive,
    t,
  ]);

  // Calculate stats
  const stats: IAppointmentStats = useMemo(() => {
    const total = appointments.length;
    const activeTherapists = new Set(appointments.map((a) => a.therapistsId))
      .size;

    return {
      totalToday: 0,
      totalMonth: total,
      confirmed: total,
      pending: 0,
      cancelled: 0,
      activeTherapists,
      totalTherapists: therapistsList.filter((t) => t.isActive).length,
    };
  }, [appointments, therapistsList]);

  const dayNames = [
    t("APPOINTMENTS_DAY_SUNDAY"),
    t("APPOINTMENTS_DAY_MONDAY"),
    t("APPOINTMENTS_DAY_TUESDAY"),
    t("APPOINTMENTS_DAY_WEDNESDAY"),
    t("APPOINTMENTS_DAY_THURSDAY"),
    t("APPOINTMENTS_DAY_FRIDAY"),
    t("APPOINTMENTS_DAY_SATURDAY"),
  ];

  // Helper function to format date as YYYY-MM-DD without timezone issues
  const formatDateString = (
    year: number,
    month: number,
    day: number,
  ): string => {
    const paddedMonth = String(month + 1).padStart(2, "0");
    const paddedDay = String(day).padStart(2, "0");
    return `${year}-${paddedMonth}-${paddedDay}`;
  };

  // Generate calendar days
  const calendarDays: IMonthDay[] = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay();
    const totalDays = lastDay.getDate();

    const days: IMonthDay[] = [];

    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDay - 1; i >= 0; i--) {
      const dayNum = prevMonthLastDay - i;
      const dateStr = formatDateString(year, month - 1, dayNum);
      const date = new Date(year, month - 1, dayNum);
      days.push({
        date: dateStr,
        dayNumber: dayNum,
        isCurrentMonth: false,
        isToday: false,
        isWeekend: date.getDay() === 5 || date.getDay() === 6,
        appointments: [],
        totalCount: 0,
      });
    }

    // Current month days
    for (let day = 1; day <= totalDays; day++) {
      const dateStr = formatDateString(year, month, day);
      const date = new Date(year, month, day);
      const dayAppointments = appointments.filter(
        (apt) => apt.date === dateStr,
      );

      days.push({
        date: dateStr,
        dayNumber: day,
        isCurrentMonth: true,
        isToday: date.getTime() === today.getTime(),
        isWeekend: date.getDay() === 5 || date.getDay() === 6,
        appointments: dayAppointments.slice(0, 3),
        totalCount: dayAppointments.length,
      });
    }

    // Next month days to fill grid
    const remainingDays = 42 - days.length; // 6 rows x 7 days
    for (let i = 1; i <= remainingDays; i++) {
      const dateStr = formatDateString(year, month + 1, i);
      const date = new Date(year, month + 1, i);
      days.push({
        date: dateStr,
        dayNumber: i,
        isCurrentMonth: false,
        isToday: false,
        isWeekend: date.getDay() === 5 || date.getDay() === 6,
        appointments: [],
        totalCount: 0,
      });
    }

    return days;
  }, [year, month, appointments]);

  // Filter appointments - apply filters but show all matching appointments for the day
  const filteredDays = calendarDays.map((day) => {
    if (!day.isCurrentMonth) return day;

    // Re-filter all appointments for this day (not just the sliced 3)
    const allDayAppointments = appointments.filter(
      (apt) => apt.date === day.date,
    );
    const filteredApts = allDayAppointments.filter((apt) => {
      if (filters.therapistId && apt.therapistsId !== filters.therapistId)
        return false;
      if (filters.companyId && apt.companyId !== filters.companyId)
        return false;
      return true;
    });

    return {
      ...day,
      appointments: filteredApts.slice(0, 3), // Show first 3 in calendar cell
      totalCount: filteredApts.length, // But show total count
    };
  });

  const handleDayClick = (day: IMonthDay) => {
    if (!day.isCurrentMonth || day.totalCount === 0) return;
    // Could navigate to daily view for this date
    console.log("Day clicked:", day.date);
  };

  const handleAppointmentClick = (
    e: React.MouseEvent,
    appointment: IAppointment,
  ) => {
    e.stopPropagation();
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleAppointmentCancelled = () => {
    reloadMonth(currentDate);
  };

  const handleOpenCreateModal = (date: string, therapistId?: number) => {
    setCreateModalDate(date);
    // Default time for monthly view click
    setCreateModalTime("09:00");
    setCreateModalTherapistId(therapistId);
    setShowCreateModal(true);
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    reloadMonth(currentDate);
    showToastMessage(
      t("MEETING_CREATED_SUCCESSFULLY") || "Appointment created successfully",
      "success",
    );
  };

  const handleMoreAppointmentsClick = (
    e: React.MouseEvent,
    day: IMonthDay,
    therapistId?: number,
  ) => {
    e.stopPropagation();
    setSelectedDay({
      ...day,
      therapistId, // Custom prop to pass to modal
    } as any);
    setIsDayModalOpen(true);
  };

  const handleCloseDayModal = () => {
    setIsDayModalOpen(false);
    setSelectedDay(null);
  };

  const handleDayAppointmentClick = (appointment: IAppointment) => {
    setIsDayModalOpen(false);
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };

  const handleMonthChange = (direction: "prev" | "next") => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + (direction === "next" ? 1 : -1));
    setCurrentDate(newDate);
    // Ensure new month is loaded
    ensureMonthLoaded(newDate);
  };

  // ================================
  // DRAG & DROP HANDLERS
  // ================================

  const calculateEndTime = (startTime: string, duration: number): string => {
    const [hours, minutes] = startTime.split(":").map(Number);
    const totalMinutes = hours * 60 + minutes + duration;
    // Handle day overflow just in case, though usually 0-23
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    return `${endHours.toString().padStart(2, "0")}:${endMinutes
      .toString()
      .padStart(2, "0")}`;
  };

  const getDayByDate = (date: string): IMonthDay | undefined => {
    return calendarDays.find((day) => day.date === date);
  };

  const getAppointmentsForDate = (date: string): IAppointment[] => {
    return appointments.filter((apt) => apt.date === date);
  };

  const getAppointmentTooltip = (apt: IAppointment): string => {
    const isGoogleEvent = apt.source === "google_calendar";
    const isOther = !apt.usersId && !apt.treatmentGroupId && !isGoogleEvent;

    let tooltipText = "";
    if (apt.time) {
      tooltipText += `${apt.time}`;
      if (apt.endTime) {
        tooltipText += ` - ${apt.endTime}`;
      } else if (apt.duration) {
        tooltipText += ` - ${calculateEndTime(apt.time, apt.duration)}`;
      }
      tooltipText += " | ";
    }
    if (isGoogleEvent) {
      tooltipText += `📅 ${apt.summary || apt.description || "Google Calendar"}`;
    } else if (isOther) {
      tooltipText += `📝 ${apt.comments || t("OTHER") || "אחר"}`;
    } else if (apt.treatmentGroupName && !apt.patientName) {
      tooltipText += `${apt.treatmentGroupName}`;
    } else {
      tooltipText += `${apt.patientName || ""}`;
    }
    if (!isGoogleEvent && !isOther && apt.therapistName) {
      tooltipText += ` | ${apt.therapistName}`;
    }
    return tooltipText;
  };

  const validateDrop = (
    targetDate: string,
  ): { status: "valid" | "invalid" | "warning"; message?: string } => {
    if (!draggedAppointment) return { status: "invalid" };

    const targetDay = getDayByDate(targetDate);
    if (!targetDay || !targetDay.isCurrentMonth) {
      return { status: "invalid", message: t("APPOINTMENTS_INVALID_DATE") };
    }

    // Always allow drop - validation will happen in the modal
    return { status: "valid" };
  };

  // Drag & Drop handlers - disabled for now, keeping for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDragStart = (
    e: React.DragEvent,
    appointment: IAppointment,
    day: IMonthDay,
  ) => {
    e.stopPropagation();
    setDraggedAppointment(appointment);
    setDraggedFromDay(day);

    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", appointment.id.toString());
    }

    const target = e.currentTarget as HTMLElement;
    setTimeout(() => {
      if (target) {
        target.classList.add(styles.dragging);
      }
    }, 0);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDragEnd = (e: React.DragEvent) => {
    const el = e.currentTarget as HTMLElement;
    if (el) {
      el.classList.remove(styles.dragging);
    }

    setDragOverDay(null);
    setDragOverStatus("valid");
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDragOver = (
    e: React.DragEvent,
    date: string,
    therapistId?: number,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedAppointment || !draggedFromDay) return;

    // Don't show drop indicator on the same slot (day AND therapist)
    if (
      date === draggedFromDay.date &&
      (!therapistId || therapistId === draggedAppointment.therapistsId)
    ) {
      setDragOverDay(null);
      setDragOverTherapistId(null);
      return;
    }

    const validation = validateDrop(date);
    setDragOverDay(date);
    setDragOverTherapistId(therapistId || null);
    setDragOverStatus(validation.status);

    e.dataTransfer.dropEffect =
      validation.status === "invalid" ? "none" : "move";
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDragLeave = (e: React.DragEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setDragOverDay(null);
      setDragOverTherapistId(null);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDrop = (
    e: React.DragEvent,
    targetDate: string,
    therapistId?: number,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedAppointment || !draggedFromDay) return;

    const targetDay = getDayByDate(targetDate);
    if (!targetDay) return;

    // Don't do anything if dropping on the same day and therapist
    if (
      targetDate === draggedFromDay.date &&
      (!therapistId || therapistId === draggedAppointment.therapistsId)
    ) {
      resetDragState();
      return;
    }

    const validation = validateDrop(targetDate);

    if (validation.status === "invalid") {
      showToastMessage(
        validation.message || t("APPOINTMENTS_CANNOT_MOVE"),
        "error",
      );
      resetDragState();
      return;
    }

    // Convert IMonthDay to IWeekDay format for the modal
    const fromDayForModal: IWeekDay = {
      date: draggedFromDay.date,
      dayName: new Date(draggedFromDay.date).toLocaleDateString(getLocale(), {
        weekday: "long",
      }),
      dayNumber: draggedFromDay.dayNumber,
      isToday: draggedFromDay.isToday,
      isWeekend: draggedFromDay.isWeekend,
    };

    const toDayForModal: IWeekDay = {
      date: targetDay.date,
      dayName: new Date(targetDay.date).toLocaleDateString(getLocale(), {
        weekday: "long",
      }),
      dayNumber: targetDay.dayNumber,
      isToday: targetDay.isToday,
      isWeekend: targetDay.isWeekend,
    };

    // Calculate end times (keeping same time, so calc based on that)
    const duration = draggedAppointment.duration || 30;
    const endTime = calculateEndTime(draggedAppointment.time, duration);

    // Show confirmation modal
    const resolvedToTherapistId =
      therapistId || draggedAppointment.therapistsId || 0;
    const targetTherapist = therapistsList.find(
      (t) => t.id === resolvedToTherapistId,
    );

    setPendingMove({
      appointment: draggedAppointment,
      fromDay: fromDayForModal,
      fromTime: draggedAppointment.time,
      fromEndTime: endTime,
      toDay: toDayForModal,
      toTime: draggedAppointment.time, // Keep same time, just change day
      toEndTime: endTime,
      toTherapistId: resolvedToTherapistId,
      toTherapistGuid: targetTherapist?.guid,
      toTherapistName:
        targetTherapist?.name || draggedAppointment.therapistName,
      warning: validation.status === "warning" ? validation.message : undefined,
    });
    setShowMoveConfirmation(true);
    setDragOverDay(null);
    setDragOverTherapistId(null);
  };

  const handleConfirmMove = async (
    newDate: string,
    newTime: string,
    newEndTime: string,
    newTherapistId: number,
  ) => {
    if (!pendingMove) return;

    try {
      const therapist = therapistsList.find((t) => t.id === newTherapistId);

      // Update appointment via API
      await patchAppointment({
        id: pendingMove.appointment.id,
        appointmentDate:
          newDate !== pendingMove.appointment.date ? newDate : undefined,
        startTime:
          newTime !== pendingMove.appointment.time ? newTime : undefined,
        endTime:
          newEndTime !== pendingMove.appointment.endTime
            ? newEndTime
            : undefined,
        therapistsGuid:
          therapist?.guid !== pendingMove.appointment.therapistsGuid
            ? therapist?.guid
            : undefined,
        therapistsId:
          newTherapistId !== pendingMove.appointment.therapistsId
            ? newTherapistId
            : undefined,
      }).unwrap();

      // Show success animation
      setShowSuccessAnimation(true);
      setTimeout(() => setShowSuccessAnimation(false), 600);

      // Show toast
      showToastMessage(
        t("APPOINTMENTS_MOVED_SUCCESS") || "Appointment moved successfully",
        "success",
      );

      // Cleanup
      setShowMoveConfirmation(false);
      setPendingMove(null);
      resetDragState();

      // Reload the month to get updated data
      reloadMonth(currentDate);
    } catch (error) {
      console.error("Failed to move appointment:", error);
      showToastMessage(
        t("APPOINTMENTS_MOVE_FAILED") || "Failed to move appointment",
        "error",
      );
    }
  };

  const handleCancelMove = () => {
    setShowMoveConfirmation(false);
    setPendingMove(null);
    resetDragState();
  };

  const resetDragState = () => {
    setDraggedAppointment(null);
    setDraggedFromDay(null);
    setDragOverDay(null);
    setDragOverTherapistId(null);
  };

  const showToastMessage = (message: string, type: "success" | "error") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const getDayClassName = (day: IMonthDay): string => {
    let className = styles.calendarDay;

    if (!day.isCurrentMonth) className += " " + styles.calendarDayOtherMonth;
    if (day.isToday) className += " " + styles.calendarDayToday;
    if (day.isWeekend) className += " " + styles.calendarDayWeekend;

    if (dragOverDay === day.date && !isTherapistFilterActive) {
      className += " " + styles.dragOver;
      if (dragOverStatus === "invalid") {
        className += " " + styles.dragOverInvalid;
      } else if (dragOverStatus === "warning") {
        className += " " + styles.dragOverWarning;
      }
    }

    return className;
  };

  const showStats = !isFullscreen || showFiltersInFullscreen;

  return (
    <div>
      {/* Statistics - hidden in fullscreen when filters are collapsed */}
      {showStats && <AppointmentStats stats={stats} viewType="monthly" />}

      {/* Calendar Section */}
      <div className={styles.calendarSection}>
        <div className={styles.calendarHeader}>
          <div className={styles.calendarHeaderLeft}>
            <h2 className={styles.calendarTitle}>
              {currentDate.toLocaleDateString(getLocale(), {
                month: "long",
                year: "numeric",
              })}
            </h2>
            <div className={styles.dragHint}>
              <span className={styles.dragHintIcon}>💡</span>
              <span>{t("APPOINTMENTS_DRAG_HINT")}</span>
            </div>
            <IconsLegend />
          </div>
          <div className={styles.calendarNav}>
            <button
              className={styles.navBtn}
              onClick={() => handleMonthChange("next")}
              aria-label="Next"
            >
              ‹
            </button>
            <button
              className={styles.todayBtn}
              onClick={() => setCurrentDate(new Date())}
            >
              {t("APPOINTMENTS_TODAY")}
            </button>
            <span className={styles.currentDate}>
              {currentDate.toLocaleDateString(getLocale(), {
                month: "long",
                year: "numeric",
              })}
            </span>
            <button
              className={styles.navBtn}
              onClick={() => handleMonthChange("prev")}
              aria-label="Previous"
            >
              ›
            </button>
            <ExportTableButtons
              disabled={appointments.length === 0}
              {...exportProps}
            />
          </div>
        </div>

        {/* Monthly Calendar Grid */}
        <div className={styles.dailyScrollContainer}>
          <div
            className={styles.monthlyCalendar}
            style={{
              minWidth: isTherapistFilterActive
                ? `${Math.max(1000, displayedTherapists.length * 150 * 7)}px`
                : "auto",
            }}
          >
            {/* Weekday Headers */}
            {dayNames.map((dayName) => (
              <div key={dayName} className={styles.weekdayHeader}>
                {dayName}
              </div>
            ))}

            {/* Calendar Days */}
            {filteredDays.map((day, index) => (
              <div
                key={`${day.date}-${index}`}
                className={getDayClassName(day)}
                onClick={() => handleDayClick(day)}
                onDragOver={(e) =>
                  day.isCurrentMonth &&
                  !isTherapistFilterActive &&
                  handleDragOver(e, day.date)
                }
                onDragLeave={handleDragLeave}
                onDrop={(e) =>
                  day.isCurrentMonth &&
                  !isTherapistFilterActive &&
                  handleDrop(e, day.date)
                }
              >
                <div
                  className={`${styles.dayNumber} ${
                    day.isToday ? styles.dayNumberToday : ""
                  }`}
                >
                  {day.dayNumber}
                  {day.isCurrentMonth && !isTherapistFilterActive && (
                    <button
                      className={styles.addAppointmentBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenCreateModal(day.date);
                      }}
                    >
                      +
                    </button>
                  )}
                </div>

                {day.isCurrentMonth && (
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      height: "calc(100% - 24px)",
                      overflow: "hidden",
                    }}
                  >
                    {isTherapistFilterActive ? (
                      displayedTherapists.map((therapist, tIndex) => {
                        // Filter appointments for this therapist and day
                        const isVirtualCalendar = therapist.id === -1;
                        const allTherapistAppointments = getAppointmentsForDate(
                          day.date,
                        ).filter((apt) => {
                          if (isVirtualCalendar) {
                            const isGoogleEvent =
                              apt.source === "google_calendar";
                            const isOther =
                              (!apt.patientName &&
                                !apt.treatmentGroupName &&
                                !apt.mediaId) ||
                              isGoogleEvent;
                            return (
                              isOther && apt.therapistsId === Number(admin.id)
                            );
                          }
                          return apt.therapistsId === therapist.id;
                        });

                        const isDragOver =
                          dragOverDay === day.date &&
                          dragOverTherapistId === therapist.id;
                        let columnClass = styles.monthlyTherapistColumn;
                        if (isDragOver) {
                          columnClass += " " + styles.dragOver;
                          if (dragOverStatus === "invalid")
                            columnClass += " " + styles.dragOverInvalid;
                          else if (dragOverStatus === "warning")
                            columnClass += " " + styles.dragOverWarning;
                        }

                        return (
                          <div
                            key={therapist.id}
                            className={columnClass}
                            style={{
                              flex: 1,
                              borderInlineEnd:
                                tIndex < displayedTherapists.length - 1
                                  ? "1px solid #E5E7EB"
                                  : "none",
                              padding: "2px",
                              display: "flex",
                              flexDirection: "column",
                              minWidth: 0,
                              borderRadius: "4px",
                            }}
                            onDragOver={
                              isVirtualCalendar
                                ? undefined
                                : (e) =>
                                    handleDragOver(e, day.date, therapist.id)
                            }
                            onDrop={
                              isVirtualCalendar
                                ? undefined
                                : (e) => handleDrop(e, day.date, therapist.id)
                            }
                            onDragLeave={
                              isVirtualCalendar ? undefined : handleDragLeave
                            }
                          >
                            <div
                              style={{
                                fontSize: "10px",
                                fontWeight: 600,
                                color:
                                  dragOverDay === day.date &&
                                  dragOverTherapistId === therapist.id
                                    ? "#059669"
                                    : "#6B7280",
                                marginBottom: "2px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                textAlign: "center",
                              }}
                              title={therapist.name}
                            >
                              {therapist.name}
                            </div>

                            <button
                              className={styles.addAppointmentBtn}
                              style={{ top: "2px", insetInlineEnd: "2px" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenCreateModal(day.date, therapist.id);
                              }}
                            >
                              +
                            </button>

                            <div
                              className={styles.dayAppointments}
                              style={{ marginTop: 0 }}
                            >
                              {allTherapistAppointments
                                .slice(0, 3)
                                .map((apt) => {
                                  const fundingColor =
                                    getAppointmentColors(apt);
                                  return (
                                    <div
                                      key={apt.id}
                                      className={styles.appointmentIndicator}
                                      style={{
                                        borderRightColor:
                                          fundingColor.borderColor,
                                        backgroundColor:
                                          fundingColor.backgroundColor,
                                        padding: "2px 4px",
                                        fontSize: "10px",
                                      }}
                                      draggable={!isVirtualCalendar}
                                      onDragStart={
                                        !isVirtualCalendar
                                          ? (e) => handleDragStart(e, apt, day)
                                          : undefined
                                      }
                                      onDragEnd={handleDragEnd}
                                      onClick={(e) =>
                                        handleAppointmentClick(e, apt)
                                      }
                                      onMouseEnter={(e) => {
                                        const r =
                                          e.currentTarget.getBoundingClientRect();
                                        setTooltip({
                                          text: getAppointmentTooltip(apt),
                                          x: r.left + r.width / 2,
                                          y: r.top,
                                        });
                                      }}
                                      onMouseLeave={() => setTooltip(null)}
                                    >
                                      {(apt.hasActionReport ||
                                        apt.hasTherapistAnswered) && (
                                        <span
                                          style={{
                                            position: "absolute",
                                            top: 2,
                                            insetInlineEnd: 2,
                                            zIndex: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 4,
                                          }}
                                        >
                                          {apt.hasActionReport && (
                                            <ActionReportBadge
                                              variant="check"
                                              size={12}
                                            />
                                          )}
                                          {apt.hasTherapistAnswered && (
                                            <ActionReportBadge
                                              variant="report"
                                              size={12}
                                            />
                                          )}
                                        </span>
                                      )}
                                      <span>{apt.time}</span>{" "}
                                      <span
                                        style={
                                          !apt.patientName &&
                                          apt.treatmentGroupName
                                            ? {
                                                color: "#10B981",
                                                fontWeight: 500,
                                              }
                                            : undefined
                                        }
                                      >
                                        {!apt.patientName &&
                                        apt.treatmentGroupName
                                          ? truncateText(apt.treatmentGroupName)
                                          : truncateText(
                                              apt.patientName || apt.comments,
                                            )}
                                      </span>
                                    </div>
                                  );
                                })}

                              {allTherapistAppointments.length > 3 && (
                                <div
                                  className={styles.moreAppointments}
                                  onClick={(e) =>
                                    handleMoreAppointmentsClick(
                                      e,
                                      day,
                                      therapist.id,
                                    )
                                  }
                                  style={{ fontSize: "10px", padding: "2px" }}
                                >
                                  +{allTherapistAppointments.length - 3}
                                </div>
                              )}

                              <div
                                style={{ flex: 1, minHeight: "10px" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenCreateModal(day.date);
                                }}
                              />
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div
                        className={styles.dayAppointments}
                        style={{ marginTop: 0, width: "100%" }}
                      >
                        {getAppointmentsForDate(day.date)
                          .slice(0, 3)
                          .map((apt) => {
                            const fundingColor = getAppointmentColors(apt);
                            return (
                              <div
                                key={apt.id}
                                className={styles.appointmentIndicator}
                                style={{
                                  borderRightColor: fundingColor.borderColor,
                                  backgroundColor: fundingColor.backgroundColor,
                                  padding: "2px 4px",
                                  fontSize: "10px",
                                }}
                                draggable
                                onDragStart={(e) =>
                                  handleDragStart(e, apt, day)
                                }
                                onDragEnd={handleDragEnd}
                                onClick={(e) => handleAppointmentClick(e, apt)}
                                onMouseEnter={(e) => {
                                  const r =
                                    e.currentTarget.getBoundingClientRect();
                                  setTooltip({
                                    text: getAppointmentTooltip(apt),
                                    x: r.left + r.width / 2,
                                    y: r.top,
                                  });
                                }}
                                onMouseLeave={() => setTooltip(null)}
                              >
                                {(apt.hasActionReport ||
                                  apt.hasTherapistAnswered) && (
                                  <span
                                    style={{
                                      position: "absolute",
                                      top: 2,
                                      insetInlineEnd: 2,
                                      zIndex: 1,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 4,
                                    }}
                                  >
                                    {apt.hasActionReport && (
                                      <ActionReportBadge
                                        variant="check"
                                        size={12}
                                      />
                                    )}
                                    {apt.hasTherapistAnswered && (
                                      <ActionReportBadge
                                        variant="report"
                                        size={12}
                                      />
                                    )}
                                  </span>
                                )}
                                <span>{apt.time}</span>{" "}
                                <span
                                  style={
                                    !apt.patientName && apt.treatmentGroupName
                                      ? { color: "#10B981", fontWeight: 500 }
                                      : undefined
                                  }
                                >
                                  {!apt.patientName && apt.treatmentGroupName
                                    ? truncateText(apt.treatmentGroupName)
                                    : truncateText(
                                        apt.patientName || apt.comments,
                                      )}
                                  {apt.therapistName &&
                                    (apt.patientName ||
                                      apt.treatmentGroupName) && (
                                      <span
                                        style={{
                                          fontSize: "8px",
                                          opacity: 0.7,
                                          marginInlineStart: "4px",
                                        }}
                                      >
                                        ({apt.therapistName})
                                      </span>
                                    )}
                                </span>
                              </div>
                            );
                          })}
                        {getAppointmentsForDate(day.date).length > 3 && (
                          <div
                            className={styles.moreAppointments}
                            onClick={(e) => handleMoreAppointmentsClick(e, day)}
                            style={{ fontSize: "10px", padding: "2px" }}
                          >
                            +{getAppointmentsForDate(day.date).length - 3}
                          </div>
                        )}
                        <div
                          style={{ flex: 1, minHeight: "10px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenCreateModal(day.date);
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Appointment Details Modal */}
      {isModalOpen && selectedAppointment && (
        <AppointmentDetailsModal
          appointment={selectedAppointment}
          onClose={handleCloseModal}
          onAppointmentCancelled={handleAppointmentCancelled}
        />
      )}

      {/* Create Appointment Modal */}
      {showCreateModal && (
        <CreateAppointmentModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
          initialDate={createModalDate}
          initialTime={createModalTime}
          therapistId={createModalTherapistId}
        />
      )}

      {/* Move Confirmation Modal */}
      {showMoveConfirmation && pendingMove && (
        <MoveConfirmationModal
          moveData={pendingMove}
          onConfirm={handleConfirmMove}
          onCancel={handleCancelMove}
        />
      )}

      {/* Day Appointments Modal */}
      {isDayModalOpen && selectedDay && (
        <DayAppointmentsModal
          date={selectedDay.date}
          appointments={appointments.filter((apt) => {
            const matchesDate = apt.date === selectedDay.date;
            const matchesTherapist = (selectedDay as any).therapistId
              ? apt.therapistsId === (selectedDay as any).therapistId
              : true;
            return matchesDate && matchesTherapist;
          })}
          onClose={handleCloseDayModal}
          onAppointmentClick={handleDayAppointmentClick}
        />
      )}

      {/* Success Animation */}
      {showSuccessAnimation && <div className={styles.successAnimation}>✓</div>}

      {/* Toast Notification */}
      {showToast && (
        <div className={styles.dragToast}>
          <div
            className={`${styles.dragToastIcon} ${
              toastType === "success"
                ? styles.dragToastIconSuccess
                : styles.dragToastIconError
            }`}
          >
            {toastType === "success" ? "✓" : "✕"}
          </div>
          <div className={styles.dragToastMessage}>{toastMessage}</div>
        </div>
      )}

      {/* Fixed Tooltip */}
      {tooltip && (
        <div
          className={styles.weeklyTooltip}
          style={{
            top: tooltip.y,
            left: tooltip.x,
            transform: "translateX(-50%) translateY(calc(-100% - 8px))",
          }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
};

export default MonthlyView;
